/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    Action,
    OnDemandEpisodesUnifiedCollectionDetail,
    UnifiedCollectionDetailType,
    UnifiedCollectionPreviewKernel,
    UnifiedCollectionScreenType,
} from 'client-code/actions';
import { SeasonsData } from 'client-code/episode-guide';
import { FeedItem } from 'client-code/feeditems-service';
import { Credit } from 'client-code/person-content-service';
import { ScreenType } from 'client-code/previews';

import { APPLICATION_FEATURE_AREA, TIVO_SERVICES } from '../../config';
import { DeviceModel } from '../../core/device';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { TransitionScreenType } from '../../core/enums/TransitionScreenType';
import { ImageAssetsService } from '../../core/infrastructure';
import { NdvrInteractor, VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { NetworkManager } from '../../core/network/NetworkManager';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TUnifiedCollectionDetail, TUnifiedCollectionScreenDetails } from '../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { ListTypes, UniqueIdentifier } from '../../core/utils';
import { CcbSwitch } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { GridTypes } from '../../core/utils/GridTypes';
import { CollectionActions } from '../../store/collectionscreen/CollectionActions';
import { CollectionFilters } from '../../store/collectionscreen/CollectionFilters';
import {
    resetAllCollectionScreenFiltersAction,
    setCollectionFilterItemLoading,
    setCollectionIsLastPage,
    setCollectionScreenFilterDetailsAction,
} from '../../store/collectionscreen/collectionscreen.actions';
import {
    COLLECTION_SCREEN_ITEMS_LIST_LIMIT,
    fetchAndUpdateCollectionDetails,
    getBrowseStickinessKey,
    getCollectionScreenInfo,
    getCurrentPagePickerStripFromSeasons,
    getEpisodeListForSelectedFilter,
    getFiltersInfo,
    getPickerStripFromSeasons,
    getSeasonsDataForSelectedFilter,
    getSelectedFilterFromPosition,
    isSeasonPickerDisplay,
    logError,
    onCursorStateChangeCollectionScreenOperations,
    prepareAndGetEpisodeList,
} from '../../store/collectionscreen/collectionscreen.reducers';
import { CollectionSortType } from '../../store/collectionscreen/CollectionSortType';
import { TCollectionScreen } from '../../store/collectionscreen/TCollectionScreen';
import { TListCollectionItem } from '../../store/collectionscreen/TListCollectionItem';
import { setGridItemsAction } from '../../store/grid/grid.actions';
import { setListEntryFocus, setListEntryPosition } from '../../store/list/list.actions';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import { getRecordingsStatus } from '../../store/recordings/recordings.reducers';
import { showGuideScreen, showTopScreen } from '../../store/screens/screens.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { hideWhisper, showWhisper } from '../../store/whisper/whisper.reducer';
import { AntiburnOsd, List, ListLabelItem, Mousetrap, ScreenFooter } from '../common';
import Grid from '../common/Grid/Grid';
import TopPreview from '../common/TopPreview/TopPreview';
import { useScreenController } from '../hooks';
import { Whisper } from '../Whisper';
import { WhisperTypes } from '../Whisper/WhisperTypes';
import { CollectionScreenItems } from './CollectionScreenItems';
import { CollectionScreenOnePassItems } from './CollectionScreenOnePassItems';
import { InteractiveLayer } from './InteractiveLayer';
import { MayAlsoLikeItem } from './MayAlsoLikeItem';
import { PersonItem } from './PersonItem';

import './CollectionScreen.scss';

const HOUR_IN_MILLISECONDS = 1 * 60 * 60 * 1000;
const SEASON_OR_YEAR_JUMP_OFFSET = 3;

const CollectionScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'CollectionScreen';

    const dispatch = useTivoDispatch();

    const collectionScreenIns = useScreenController<TCollectionScreen>({
        properties: {
            screenController: ScreenControllerTypes.COLLECTION_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS,
        },
    });

    const isScreenMounted = useRef(false);
    const isVisitedUpcomingFilter = useRef(false);
    const onePassActions = useRef([] as string[]);
    const moreInfoActions = useRef([] as string[]);
    const isModifiedOnepassAction = useRef<boolean>(false);
    const isDeletedOnepassAction = useRef<boolean>(false);
    const [filters, setFilters] = useState([] as string[]);
    const [initialFocus, setInitialFocus] = useState(false);
    const [onePassItems, setOnePassItems] = useState([] as string[]);
    const [moreInfoItems, setMoreInfoItems] = useState([] as string[]);
    // TODO: Replace with real cast data KST-12836
    // eslint-disable-next-line no-unused-vars
    const [castState, setCastState] = useState<{
        loading: boolean;
        error: string | null;
        items: Credit[];
    }>({
        loading: false,
        error: null,
        items: [],
    });
    // eslint-disable-next-line no-unused-vars
    const [mayAlsoLikeItems, setMayAlsoLikeItems] = useState<{
        loading: boolean;
        error: string | null;
        items: FeedItem[];
    }>({
        loading: false,
        error: null,
        items: [],
    });
    const [previewForUCId, setPreviewForUCId] = useState<any>({});
    const [selectedFilter, setSelectedFilter] = useState(CollectionFilters.NONE);
    const [isCursorActive, setIsCursorActive] = useState<boolean>(CursorManager.isCursorAppeared());
    const [refreshCollectionScreen, setRefreshCollectionScreen] = useState(false);
    const [completeOfferPreviewReceived, setCompleteOfferPreviewReceived] = useState(false);

    const filterslistEntry = useTivoSelector(
        (state: RootState) => state.list[ListTypes.COLLECTION_FILTERS],
    );
    const itemslistEntry = useTivoSelector(
        (state: RootState) => state.list[ListTypes.COLLECTION_ITEMS],
    );
    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const collectionScreenState = useTivoSelector((state: RootState) => state.collectionScreen);
    const filterPosition = filterslistEntry?.position;
    const { episodes, sortOrder } = collectionScreenState.filters[selectedFilter];
    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);

    const ndvrServices = new NdvrInteractor();
    const secondaryBranding = ndvrServices.getAppUiImageByKey(
        BrandingUiInfoKeys.APP_ICONS.SECONDARY_BRANDING,
    );

    const onCursorStateChangeCallback = (state: boolean) => {
        setIsCursorActive(state);
        dispatch(onCursorStateChangeCollectionScreenOperations());
    };

    const getContentIdSelection = (position: number): string => {
        const item = episodes?.[position];
        if (!item || !item?.contentId) {
            return '';
        }

        switch (selectedFilter) {
            case CollectionFilters.ALL_EPISODE_DETAIL:
            case CollectionFilters.WATCH_LIST_DETAILS:
            case CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL:
                return item.contentId;
            case CollectionFilters.RECORDINGS_DETAILS:
                if ('recordingId' in item) return item.recordingId || '';
                break;
            case CollectionFilters.UPCOMING_DETAIL:
                if ('offerId' in item) return item.offerId;
                break;
            default:
                break;
        }
        return '';
    };

    const getOnePassOptionsFromActions = (actions: Action[]) => {
        if (actions.length) {
            const collectionOnePassSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_PREF_KEY,
            ).edit();
            collectionOnePassSharedPreferenceEditor.putJson(
                SharedPrefConstants.COLLECTION_ONE_PASS_ACTION_LIST,
                actions as any,
            );
            collectionOnePassSharedPreferenceEditor.apply();
        }

        const nonDeletedActions = actions.filter(
            (action) => action?.kernel?.actionId !== CollectionActions.DELETE_ALL_EPISODES,
        );
        return nonDeletedActions.length
            ? nonDeletedActions.map((action) => {
                  const actionId = action?.kernel?.actionId || '';
                  switch (actionId) {
                      case CollectionActions.CANCEL_ONEPASS_V2:
                          return Locale.getString(LocaleKeys.CANCEL_ONEPASS_OVERLAY_TITLE);
                      case CollectionActions.CREATE_ONEPASS_V2:
                          return Locale.getString(LocaleKeys.CREATE_ONEPASS_OVERLAY_TITLE);
                      case CollectionActions.MODIFY_ONEPASS_V2:
                          return Locale.getString(LocaleKeys.MODIFY_ONEPASS_OVERLAY_TITLE);
                      default:
                          return '';
                  }
              })
            : [];
    };

    const getUpcomingFilterTitle = (collection: TUnifiedCollectionDetail) => {
        const filtersInfo = getFiltersInfo();
        if (
            (collection.presentation?.hasLiveOffers &&
                collection.presentation?.hasUpcomingOffers) ||
            filtersInfo?.unifiedCollectionScreenType ===
                UnifiedCollectionScreenType.TeamScreenDetails ||
            filtersInfo?.unifiedCollectionScreenType ===
                UnifiedCollectionScreenType.LeagueScreenDetails
        ) {
            return Locale.getString(LocaleKeys.COLLECTION_FILTER_LIVE_AND_UPCOMING_TITLE);
        }
        if (collection.presentation?.hasUpcomingOffers) {
            return Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_TITLE);
        }
        return Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_NONE_TITLE);
    };

    const getFiltersFromCollection = (collection: TUnifiedCollectionDetail): string => {
        let collectionFilterTitle = '';
        const presentationId = collection.presentation?.presentationId || '';

        if (collection.actions) {
            if (
                presentationId === CollectionFilters.ONEPASS_OPTIONS_DETAIL ||
                presentationId === CollectionFilters.GET_THIS_SHOW_DETAILS
            ) {
                onePassActions.current = getOnePassOptionsFromActions(collection.actions);
            } else if (presentationId === CollectionFilters.MORE_INFO_DETAIL) {
                moreInfoActions.current = collection.actions
                    .filter((action) => action?.kernel?.actionId === CollectionActions.MORE_INFO)
                    .map(() => Locale.getString(LocaleKeys.MORE_INFO_DETAIL));
            }
        }

        if (
            collection.detailType ===
            UnifiedCollectionDetailType.UpcomingEpisodesUnifiedCollectionDetail
        ) {
            collectionFilterTitle = getUpcomingFilterTitle(collection);
        } else {
            collectionFilterTitle = presentationId
                ? Locale.getString(LocaleKeys[presentationId])
                : '';
        }
        return collectionFilterTitle;
    };

    const getFiltersFromCollectionDetails = (
        collectionFilters: TUnifiedCollectionScreenDetails,
    ): string[] => {
        if (collectionFilters?.detailList?.details) {
            const modifiedCollectionFilters = { ...collectionFilters };
            let filteredCollection = collectionFilters.detailList.details;
            const iscastEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_CAST_SLT_REMOVE_IN_KST_12418,
            );
            const isMayAlsoLikeEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_MAY_ALSO_LIKE_SLT_REMOVE_IN_KST_12418,
            );
            if (!isMayAlsoLikeEnabled) {
                filteredCollection = filteredCollection.filter(
                    (cl) =>
                        cl.detailType !==
                        UnifiedCollectionDetailType.MayAlsoLikeUnifiedCollectionDetail,
                );
            }
            if (!iscastEnabled) {
                filteredCollection = filteredCollection.filter(
                    (cl) =>
                        cl.detailType !== UnifiedCollectionDetailType.CastUnifiedCollectionDetail,
                );
            }

            modifiedCollectionFilters.detailList.details = [...filteredCollection];
            const collectionFiltersSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.COLLECTION_FILTERS_PREF_KEY,
            ).edit();
            collectionFiltersSharedPreferenceEditor.putJson(
                SharedPrefConstants.COLLECTION_FILTERS_INFO,
                modifiedCollectionFilters as any,
            );
            collectionFiltersSharedPreferenceEditor.apply();
            return filteredCollection.map(getFiltersFromCollection);
        }
        return [];
    };

    const fetchFilters = async () => {
        try {
            const {
                unifiedCollectionId = '',
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
            } = getCollectionScreenInfo() || {};
            const collectionFilters = await ndvrServices.getUCScreenDetailsActionsV2({
                unifiedCollectionId,
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
            });
            if (isScreenMounted.current) {
                const filterList = getFiltersFromCollectionDetails(collectionFilters);
                setFilters(filterList);
            }
        } catch (error: any) {
            logError(TAG, error);
            setFilters([]);
            ScreenController.getInstance().popAllMatching(LocationUrls.COLLECTION_SCREEN);
            dispatch(showTopScreen());
        } finally {
            if (refreshCollectionScreen) {
                setRefreshCollectionScreen(false);
            }
        }
    };

    const setCollectionScreenFilterDetails = (
        selectedCollectionFilter: string,
        episodesList: TListCollectionItem[] = [],
        newOffset: number = 0,
        newLimit: number = 0,
        totalItems: number = 0,
    ) => {
        dispatch(
            setCollectionScreenFilterDetailsAction(selectedCollectionFilter, {
                episodes: episodesList,
                offset: newOffset,
                limit: newLimit,
                totalItems,
            }),
        );
    };

    const fetchEpisodes = async (selectedCollectionFilter: string) => {
        try {
            const {
                lastPosition,
                limit,
                offset,
                sortOrder: lSortOrder,
            } = collectionScreenState.filters[selectedCollectionFilter];
            const newlimit = offset === 0 ? COLLECTION_SCREEN_ITEMS_LIST_LIMIT - 1 : limit;
            const episodesRes = await getEpisodeListForSelectedFilter(
                selectedCollectionFilter,
                offset,
                newlimit,
                lSortOrder,
            );

            const networkCallId = UniqueIdentifier.getCollectionFilterNetworkCallId();
            if (episodesRes.networkCallId !== networkCallId) return;

            const seasonData = await getSeasonsDataForSelectedFilter(
                selectedCollectionFilter,
                lSortOrder,
            );

            let episodesList = episodesRes.episodeInfo;
            if (isSeasonPickerDisplay(seasonData, lSortOrder)) {
                episodesList = prepareAndGetEpisodeList(seasonData, episodesList, offset, newlimit);
            }
            setCollectionScreenFilterDetails(
                selectedCollectionFilter,
                episodesList as TListCollectionItem[],
                offset,
                offset === 0 ? COLLECTION_SCREEN_ITEMS_LIST_LIMIT - 1 : limit,
                seasonData.totalEpisodesCount - 1,
            );
            dispatch(setCollectionFilterItemLoading(false));
            if (lastPosition && lastPosition !== -1 && lastPosition <= episodesList?.length - 1) {
                dispatch(setListEntryPosition(ListTypes.COLLECTION_ITEMS, lastPosition));
            } else {
                dispatch(
                    setListEntryPosition(
                        ListTypes.COLLECTION_ITEMS,
                        isSeasonPickerDisplay(seasonData, lSortOrder) ? 1 : 0,
                    ),
                );
            }
        } catch (error: any) {
            dispatch(setCollectionFilterItemLoading(false));
            setCollectionScreenFilterDetails(selectedCollectionFilter);
            logError(TAG, error);
        }
    };

    const fetchUpcoming = async () => {
        try {
            const collectionInfoSharedPref = new SharedPreferences(
                SharedPrefConstants.COLLECTION_INFO_PREF_KEY,
            );

            let requestToken;
            let minEndTime = new Date().toISOString();

            if (isVisitedUpcomingFilter.current) {
                const upcomingInfo = collectionInfoSharedPref.getJson(
                    SharedPrefConstants.COLLECTION_UPCOMING_FILTER_INFO,
                ) as unknown as { minEndTime: string; requestToken: string };
                const minEndTimeInMs = new Date(upcomingInfo.minEndTime).getTime();
                const isMinEndTimeValid = Date.now() - minEndTimeInMs < HOUR_IN_MILLISECONDS;
                if (!isMinEndTimeValid) {
                    isVisitedUpcomingFilter.current = false;
                } else minEndTime = upcomingInfo.minEndTime;
                requestToken = upcomingInfo.requestToken;
            }

            const { lastPosition, limit, offset } =
                collectionScreenState.filters[CollectionFilters.UPCOMING_DETAIL];
            const newlimit = offset === 0 ? COLLECTION_SCREEN_ITEMS_LIST_LIMIT - 1 : limit;

            const res = await ndvrServices.getUpcomingOffersForCollectionId({
                objectId: getCollectionScreenInfo()?.unifiedCollectionId || '',
                minEndTime,
                offset,
                limit: newlimit,
                screenType: APPLICATION_FEATURE_AREA.SERIES_SCREEN,
                requestToken,
                networkCallId: UniqueIdentifier.getNextCollectionFilterNetworkCallId(),
            });

            const networkCallId = UniqueIdentifier.getCollectionFilterNetworkCallId();
            if (res.networkCallId !== networkCallId) return;

            const isUpcomingNone =
                filters[filterPosition] ===
                Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_NONE_TITLE);
            if (res?.totalCount === 0 && !isUpcomingNone) {
                const modifiedFilters = filters.map((filter) => {
                    if (
                        filter ===
                        Locale.getString(LocaleKeys.COLLECTION_FILTER_LIVE_AND_UPCOMING_TITLE)
                    ) {
                        return Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_NONE_TITLE);
                    }
                    return filter;
                });
                setFilters(modifiedFilters);
            }

            if (!isVisitedUpcomingFilter.current) {
                const collectionInfoSharedPrefEditor = collectionInfoSharedPref.edit();
                collectionInfoSharedPrefEditor.putJson(
                    SharedPrefConstants.COLLECTION_UPCOMING_FILTER_INFO,
                    {
                        minEndTime,
                        requestToken: res.responseToken,
                    } as unknown as JSON,
                );
                collectionInfoSharedPrefEditor.commit();
                isVisitedUpcomingFilter.current = true;
            }

            setCollectionScreenFilterDetails(
                CollectionFilters.UPCOMING_DETAIL,
                res.upcomingOffers,
                offset,
                newlimit,
                res.totalCount - 1,
            );

            dispatch(setCollectionFilterItemLoading(false));

            if (lastPosition && lastPosition !== -1 && lastPosition <= res?.totalCount - 1)
                dispatch(setListEntryPosition(ListTypes.COLLECTION_ITEMS, lastPosition));
            else dispatch(setListEntryPosition(ListTypes.COLLECTION_ITEMS, 0));
        } catch (error: any) {
            dispatch(setCollectionFilterItemLoading(false));
            setCollectionScreenFilterDetails(CollectionFilters.UPCOMING_DETAIL);
            logError(TAG, error);
        }
    };

    const fetchCastDetails = async () => {
        try {
            setCastState({ loading: true, error: null, items: [] });
            const res = await new VodInteractor().getCollectionCastCredits(
                APPLICATION_FEATURE_AREA.SERIES_SCREEN,
                getCollectionScreenInfo()?.unifiedCollectionId || '',
            );
            setCastState({ loading: false, error: null, items: res });
            dispatch(
                setGridItemsAction(GridTypes.CAST, {
                    items: res,
                    isActive: false,
                    focusedItem: {
                        row: 0,
                        column: 0,
                    },
                    rowLimit: 3,
                    columnLimit: 5,
                    ghostMode: false,
                }),
            );
        } catch (error) {
            setCastState({
                loading: false,
                error: Locale.getString(LocaleKeys.EMPTY_LIST_TEXT),
                items: [],
            });
        }
    };

    const fetchMayAlsoLikeDetails = async () => {
        try {
            setMayAlsoLikeItems({ loading: true, error: null, items: [] });
            const res = await new VodInteractor().fetchCollectionMayAlsoLike(
                APPLICATION_FEATURE_AREA.SERIES_SCREEN,
                getCollectionScreenInfo()?.unifiedCollectionId || '',
            );
            setMayAlsoLikeItems({ loading: false, error: null, items: res.feedItems });
        } catch (error) {
            setMayAlsoLikeItems({
                loading: false,
                error: Locale.getString(LocaleKeys.EMPTY_LIST_TEXT),
                items: [],
            });
        }
    };

    const handleRecordingStatusUpdate = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.RECORDING_DELETED:
            case UIOrchestratorState.ONE_PASS_CREATED:
            case UIOrchestratorState.ONE_PASS_DELETED:
            case UIOrchestratorState.ONE_PASS_MODIFIED:
                dispatch(getRecordingsStatus());
                break;
            default:
        }
    };

    const handleCollectionScreenRefresh = (state: UIOrchestratorState) => {
        if (
            state === UIOrchestratorState.ONE_PASS_CREATED ||
            state === UIOrchestratorState.ONE_PASS_MODIFIED ||
            state === UIOrchestratorState.ONE_PASS_DELETED
        ) {
            setRefreshCollectionScreen(true);
            isModifiedOnepassAction.current = state === UIOrchestratorState.ONE_PASS_MODIFIED;
            isDeletedOnepassAction.current = state === UIOrchestratorState.ONE_PASS_DELETED;
            dispatch(setCollectionFilterItemLoading(true));
            setInitialFocus(false);
        }
        handleRecordingStatusUpdate(state);
    };

    const handleOnePassFilterItems = (filter: string) => {
        const isGetThisShowSelected = filter === Locale.getString(LocaleKeys.GET_THIS_SHOW_DETAILS);
        const selectOnePassFilter = isGetThisShowSelected
            ? CollectionFilters.GET_THIS_SHOW_DETAILS
            : CollectionFilters.ONEPASS_OPTIONS_DETAIL;
        const { lastPosition } = collectionScreenState.filters[selectOnePassFilter];
        setSelectedFilter(selectOnePassFilter);
        setOnePassItems(onePassActions.current);
        // directly set value as no async operation
        dispatch(setCollectionFilterItemLoading(false));
        if (lastPosition && lastPosition !== -1 && onePassActions.current?.length > 1) {
            dispatch(setListEntryPosition(ListTypes.COLLECTION_ONEPASS_ITEMS, lastPosition));
        } else {
            dispatch(setListEntryPosition(ListTypes.COLLECTION_ONEPASS_ITEMS, 0));
        }
    };

    const onNetworkStateChangeCallback = () => {
        if (NetworkManager.isNetworkDisconnected()) {
            const message = NetworkManager.getNetworkDisconnectedMessage();
            dispatch(hideOverlayDialog());
            dispatch(showWhisper({ message }));
        } else {
            dispatch(hideWhisper());
        }
    };

    const fetchItems = async () => {
        let filter = filters[filterPosition] || '';
        if (!initialFocus) {
            filter = filters[updateAndGetInitialFilterFocus()];
        }
        switch (filter) {
            case Locale.getString(LocaleKeys.ALL_EPISODE_DETAIL):
                setSelectedFilter(CollectionFilters.ALL_EPISODE_DETAIL);
                await fetchEpisodes(CollectionFilters.ALL_EPISODE_DETAIL);
                break;
            case Locale.getString(LocaleKeys.RECORDINGS_DETAILS):
                setSelectedFilter(CollectionFilters.RECORDINGS_DETAILS);
                await fetchEpisodes(CollectionFilters.RECORDINGS_DETAILS);
                break;
            case Locale.getString(LocaleKeys.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL):
                setSelectedFilter(CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL);
                await fetchEpisodes(CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL);
                break;
            case Locale.getString(LocaleKeys.WATCH_LIST_DETAILS):
                setSelectedFilter(CollectionFilters.WATCH_LIST_DETAILS);
                await fetchEpisodes(CollectionFilters.WATCH_LIST_DETAILS);
                break;
            case Locale.getString(LocaleKeys.COLLECTION_FILTER_LIVE_AND_UPCOMING_TITLE):
            case Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_NONE_TITLE):
            case Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_TITLE):
                setSelectedFilter(CollectionFilters.UPCOMING_DETAIL);
                await fetchUpcoming();
                break;
            case Locale.getString(LocaleKeys.GET_THIS_SHOW_DETAILS):
            case Locale.getString(LocaleKeys.ONEPASS_OPTIONS_DETAIL):
                handleOnePassFilterItems(filter);
                break;
            case Locale.getString(LocaleKeys.MORE_INFO_DETAIL):
                setSelectedFilter(CollectionFilters.MORE_INFO_DETAIL);
                setMoreInfoItems(moreInfoActions.current);
                // directly set value as no async operation
                dispatch(setCollectionFilterItemLoading(false));
                break;
            case Locale.getString(LocaleKeys.CAST_DETAIL):
                setSelectedFilter(CollectionFilters.CAST_DETAIL);
                await fetchCastDetails();
                break;
            case Locale.getString(LocaleKeys.MAY_ALSO_LIKE_DETAIL):
                setSelectedFilter(CollectionFilters.MAY_ALSO_LIKE_DETAIL);
                await fetchMayAlsoLikeDetails();
                break;
            default:
        }
    };

    const getTopPreviewForVodContent = (contentId) => {
        const filtersInfo = getFiltersInfo();
        const detail = filtersInfo.detailList.details[
            filterPosition
        ] as unknown as OnDemandEpisodesUnifiedCollectionDetail;
        const previewKernel = detail?.previewKernel as UnifiedCollectionPreviewKernel;
        new VodInteractor()
            .fetchContentPreviewV3({
                contentId,
                partnerId: previewKernel.partnerId || detail.partnerId,
                collapsedVodItemId: detail.collapsedVodItemId,
                contentSupplierPartnerId: previewKernel.contentSupplierPartnerId,
            })
            .then((response) => {
                setPreviewForUCId(response);
                updateCollectionPreview(response);
            })
            .catch((error: any) => {
                setPreviewForUCId({});
                updateCollectionPreview({});
                logError(TAG, error);
            });
    };

    const fetchTopPreview = (selectedId: string) => {
        if (!selectedId) return;
        switch (selectedFilter) {
            case CollectionFilters.ALL_EPISODE_DETAIL:
            case CollectionFilters.WATCH_LIST_DETAILS:
                getTopPreviewForEpisode(selectedId);
                break;
            case CollectionFilters.RECORDINGS_DETAILS:
                getTopPreviewForRecording(selectedId);
                break;
            case CollectionFilters.UPCOMING_DETAIL:
                getTopPreviewForUpcomingOffers(selectedId);
                break;
            case CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL:
                getTopPreviewForVodContent(selectedId);
                break;
            default:
                getTopPreviewForUCId();
                break;
        }
    };

    const renderFilterItem = (item: any, itemClass: string, index: number) => {
        return (
            <Mousetrap
                key={`filter-item-${index}`}
                value={`${index}_${ListTypes.COLLECTION_FILTERS}`}
                className={`filter-item ${itemClass}`}
            >
                <ListLabelItem label={item} />
            </Mousetrap>
        );
    };

    const renderItems = () => {
        if (
            selectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS ||
            selectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL
        ) {
            return (
                <CollectionScreenOnePassItems
                    selectedFilter={selectedFilter}
                    onePassItems={onePassItems}
                />
            );
        }
        if (selectedFilter === CollectionFilters.MORE_INFO_DETAIL) {
            return (
                <div className="cs-onepass-items-layout cs-moreinfo-items-layout">
                    <List
                        type={ListTypes.COLLECTION_MORE_INFO}
                        itemList={moreInfoItems}
                        generateItemView={(item, itemClass, index) => (
                            <Mousetrap
                                key={`seeMore-item-${index}`}
                                value={`${index}_${ListTypes.COLLECTION_MORE_INFO}`}
                                className={`item ${itemClass}`}
                            >
                                <ListLabelItem
                                    label={item}
                                    customContainerStyle="cs-item-moreinfo-container"
                                    customLabelStyle="cs-item-moreinfo-title"
                                />
                            </Mousetrap>
                        )}
                    />
                </div>
            );
        }

        if (selectedFilter === CollectionFilters.CAST_DETAIL) {
            const imageService = new ImageAssetsService({
                imageHostUrl: VodInteractor.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            });
            return (
                <div className="cs-cast-items-layout">
                    {castState.error && <div className="cs-empty-grid">{castState.error}</div>}
                    {!castState.error && castState.loading && null}
                    {!castState.error && !castState.loading && castState.items?.length && (
                        <Grid
                            columns={5}
                            rows={3}
                            horizontalGap="0.52vw"
                            verticalGap="0.93vh"
                            className="cast-grid"
                        >
                            {castState.items?.map((cast: any, index) => {
                                const imageUrl = imageService.getPersonImageUrl(cast.personId);
                                return (
                                    <PersonItem
                                        key={`cast-${index}`}
                                        title={cast.name}
                                        imageUrl={imageUrl}
                                        role={cast.role}
                                        personId={cast.personId}
                                        index={index}
                                        gridType={GridTypes.CAST}
                                    />
                                );
                            })}
                        </Grid>
                    )}
                    {!castState.error && !castState.loading && !castState.items?.length && (
                        <div className="cs-empty-grid">
                            {Locale.getString(LocaleKeys.EMPTY_LIST_TEXT)}
                        </div>
                    )}
                </div>
            );
        }

        if (
            selectedFilter === CollectionFilters.MAY_ALSO_LIKE_DETAIL &&
            mayAlsoLikeItems?.items?.length
        ) {
            return (
                <div className="may-also-like-items-layout">
                    <Grid
                        columns={3}
                        rows={3}
                        horizontalGap="0.52vw"
                        verticalGap="0.93vh"
                        className="mayAlsoLike-grid"
                    >
                        {mayAlsoLikeItems.items.map((mayAlsoLikeItem: any, index: number) => (
                            <MayAlsoLikeItem
                                key={`mayAlsoLike-${index}`}
                                title={mayAlsoLikeItem.title}
                                imageUrl={''} // Placeholder image
                                feedItemId={mayAlsoLikeItem.feedItemId}
                                altFeedItemId={mayAlsoLikeItem.altFeedItemId}
                                feedItemType={mayAlsoLikeItem.feedItemType}
                            />
                        ))}
                    </Grid>
                </div>
            );
        }
        return <CollectionScreenItems selectedFilter={selectedFilter} />;
    };

    const updateCollectionPreview = (response: any) => {
        const collectionPreviewSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.COLLECTION_PREVIEW,
        ).edit();
        collectionPreviewSharedPreferenceEditor.putJson(
            SharedPrefConstants.COLLECTION_PREVIEW,
            response as any,
        );
        collectionPreviewSharedPreferenceEditor.apply();
    };

    const getTopPreviewForUCId = () => {
        const {
            unifiedCollectionId = '',
            partnerId,
            contentSupplierPartnerId,
        } = getCollectionScreenInfo() || {};

        new NdvrInteractor()
            .getPreviewForUCId({
                unifiedCollectionId,
                partnerId,
                contentSupplierPartnerId,
            })
            .then((response) => {
                setPreviewForUCId(response);
                updateCollectionPreview(response);
            })
            .catch((error) => {
                updateCollectionPreview({});
                setPreviewForUCId({});
                Log.e(TAG, error);
            })
            .finally(() => {
                setCompleteOfferPreviewReceived(true);
            });
    };

    const getTopPreviewForEpisode = (contenId) => {
        new NdvrInteractor()
            .getContentPreviewV2ForContentId(contenId)
            .then((response) => {
                setPreviewForUCId(response);
                updateCollectionPreview(response);
            })
            .catch((error: any) => {
                setPreviewForUCId({});
                updateCollectionPreview({});
                logError(TAG, error);
            });
    };

    const getTopPreviewForRecording = (recordingId) => {
        new NdvrInteractor()
            .fetchRecordingPreview({
                recordingId: recordingId as string,
                applicationFeatureArea: 'collectionScreen',
            })
            .then((response) => {
                setPreviewForUCId(response);
                updateCollectionPreview(response);
            })
            .catch((error: any) => {
                Log.e(TAG, error);
                setPreviewForUCId({});
                updateCollectionPreview({});
                logError(TAG, error);
            });
    };

    const getTopPreviewForUpcomingOffers = (offerId) => {
        new NdvrInteractor()
            .fetchOfferPreviewV2(offerId, ScreenType.SeriesScreenItem)
            .then((response) => {
                setPreviewForUCId(response);
                updateCollectionPreview(response);
            })
            .catch((error: any) => {
                updateCollectionPreview({});
                setPreviewForUCId({});
                logError(TAG, error);
            });
    };

    const getPositionBasedOnTransitionScreen = (): number => {
        let selectedPosition = 0;
        const { transitionScreen = '' } = getCollectionScreenInfo() || {};
        const filtersInfo = getFiltersInfo();
        filtersInfo?.detailList?.details?.forEach((detail, index) => {
            const id = detail.presentation?.presentationId;
            if (
                transitionScreen === TransitionScreenType.ONEPASS_MANAGER &&
                id === CollectionFilters.ONEPASS_OPTIONS_DETAIL
            ) {
                selectedPosition = index;
            }
        });
        return selectedPosition;
    };

    const getInitialFilterPosition = (): number => {
        let selectedPosition = -1;
        const filtersInfo = getFiltersInfo();

        const detailsArray = filtersInfo?.detailList?.details || [];
        const isDeletedOnepassActionInVodGarden =
            isDeletedOnepassAction.current && !!getCollectionScreenInfo()?.partnerId;

        for (let i = 0; i < detailsArray.length; i += 1) {
            const detail = detailsArray[i];
            /* After modifying a one-pass, highlight the ONEPASS_OPTIONS_DETAIL filter.
               After cancelling a one-pass in a VOD walled garden,
               highlight the GET_THIS_SHOW_DETAILS filter and ignore the isSelected property.
             */
            if (
                (!isModifiedOnepassAction.current &&
                    !isDeletedOnepassActionInVodGarden &&
                    detail.presentation?.isSelected) ||
                (isModifiedOnepassAction.current &&
                    detail.presentation?.presentationId ===
                        CollectionFilters.ONEPASS_OPTIONS_DETAIL) ||
                (isDeletedOnepassActionInVodGarden &&
                    detail.presentation?.presentationId === CollectionFilters.GET_THIS_SHOW_DETAILS)
            ) {
                selectedPosition = i;
                break;
            }
        }

        return selectedPosition;
    };

    const updateAndGetInitialFilterFocus = (): number => {
        let initialFilterPosition = getInitialFilterPosition();
        const { lastFilterPosition } = collectionScreenIns?.screen?.getProperties() || {};
        if (lastFilterPosition !== undefined) {
            initialFilterPosition = lastFilterPosition;
        } else if (initialFilterPosition === -1) {
            initialFilterPosition = getPositionBasedOnTransitionScreen();
        }
        dispatch(setListEntryPosition(ListTypes.COLLECTION_FILTERS, initialFilterPosition));
        return initialFilterPosition;
    };

    const updateItemListPosition = (position: number) => {
        dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
        dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, true));
        dispatch(setListEntryPosition(ListTypes.COLLECTION_ITEMS, position));
        let selectedId = '';
        if (itemslistEntry?.position !== undefined)
            selectedId = getContentIdSelection(itemslistEntry?.position);
        fetchTopPreview(selectedId);
    };

    const handleInitialItemFocus = async (initialSelectedFilter: string) => {
        try {
            const { sortOrder: newSortOrder } =
                collectionScreenState.filters[initialSelectedFilter];
            let seasonData = {} as SeasonsData;
            if (
                initialSelectedFilter !== CollectionFilters.GET_THIS_SHOW_DETAILS &&
                initialSelectedFilter !== CollectionFilters.ONEPASS_OPTIONS_DETAIL &&
                initialSelectedFilter !== CollectionFilters.UPCOMING_DETAIL &&
                initialSelectedFilter !== CollectionFilters.MORE_INFO_DETAIL
            ) {
                seasonData = await getSeasonsDataForSelectedFilter(
                    initialSelectedFilter,
                    newSortOrder,
                );
            }
            const { lastOffset, lastItemPosition } =
                collectionScreenIns?.screen?.getProperties() || {};
            const collectionSharedPreference = DeviceModel.getSharedPreferences(
                SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_PREF_KEY,
            );
            const stickinessInfo = collectionSharedPreference.getJson(
                SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_INFO,
            ) as any;
            const { unifiedCollectionId = '', partnerId = '' } = getCollectionScreenInfo() || {};
            const key = getBrowseStickinessKey(unifiedCollectionId, partnerId);

            if (
                initialSelectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS ||
                initialSelectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL
            ) {
                dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_ONEPASS_ITEMS, true));
                dispatch(setListEntryPosition(ListTypes.COLLECTION_ONEPASS_ITEMS, 0));
            } else if (initialSelectedFilter === CollectionFilters.MORE_INFO_DETAIL) {
                dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, false));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_MORE_INFO, true));
                dispatch(setListEntryPosition(ListTypes.COLLECTION_MORE_INFO, 0));
                // Since we store the last position in stickiness info for more info filter
                if (lastOffset !== undefined && lastItemPosition !== undefined)
                    collectionScreenIns?.screen?.setProperties({
                        ...(collectionScreenIns?.screen?.getProperties() ||
                            ({} as TCollectionScreen)),
                        lastFilterPosition: undefined,
                        lastItemPosition: undefined,
                        lastOffset: undefined,
                    });
            } else if (!episodes?.length) {
                dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
            } else if (lastOffset !== undefined && lastItemPosition !== undefined) {
                dispatch(fetchAndUpdateCollectionDetails(false, lastOffset, lastItemPosition));
                const collectionScreenProps =
                    collectionScreenIns?.screen?.getProperties() || ({} as TCollectionScreen);
                collectionScreenIns?.screen?.setProperties({
                    ...collectionScreenProps,
                    lastFilterPosition: undefined,
                    lastItemPosition: undefined,
                    lastOffset: undefined,
                });
            } else if (seasonData?.stickyInfo?.seasonOrYearAsc) {
                const { seasonOrYear, episodeIndex } = seasonData.stickyInfo.seasonOrYearAsc;
                const pickerStripMap = getPickerStripFromSeasons(seasonData);
                const selectedPickerStrip = pickerStripMap.get(seasonOrYear);
                if (!selectedPickerStrip) {
                    updateItemListPosition(1);
                    return;
                }
                const initialOffset =
                    selectedPickerStrip.stripPosition + episodeIndex - SEASON_OR_YEAR_JUMP_OFFSET;
                const pickerStrips = getCurrentPagePickerStripFromSeasons(
                    seasonData,
                    initialOffset,
                    COLLECTION_SCREEN_ITEMS_LIST_LIMIT,
                );
                const noOfPickerStrips = pickerStrips.length;
                const initialPosition = noOfPickerStrips
                    ? SEASON_OR_YEAR_JUMP_OFFSET + noOfPickerStrips
                    : SEASON_OR_YEAR_JUMP_OFFSET;
                if (initialOffset <= 0) {
                    updateItemListPosition(
                        isSeasonPickerDisplay(seasonData, newSortOrder)
                            ? episodeIndex + 1
                            : episodeIndex,
                    );
                } else {
                    dispatch(
                        fetchAndUpdateCollectionDetails(false, initialOffset, initialPosition),
                    );
                }
            } else if (seasonData?.stickyInfo?.dateDesc) {
                const { episodeIndex } = seasonData.stickyInfo.dateDesc;
                const initialOffset = episodeIndex - SEASON_OR_YEAR_JUMP_OFFSET;
                const initialPosition = SEASON_OR_YEAR_JUMP_OFFSET;
                if (initialOffset <= 0) {
                    updateItemListPosition(episodeIndex);
                } else {
                    dispatch(
                        fetchAndUpdateCollectionDetails(false, initialOffset, initialPosition),
                    );
                }
            } else if (
                stickinessInfo &&
                key &&
                stickinessInfo[key] &&
                stickinessInfo[key][initialSelectedFilter].lastPosition !== -1
            ) {
                const noOfItems = collectionScreenState.filters[initialSelectedFilter].totalItems;
                const { lastPosition } = stickinessInfo[key][initialSelectedFilter];
                const position =
                    noOfItems && lastPosition <= Math.max(noOfItems, episodes?.length || 0)
                        ? lastPosition
                        : 0;
                updateItemListPosition(position);
            } else if (isSeasonPickerDisplay(seasonData, newSortOrder)) {
                updateItemListPosition(1);
            } else {
                updateItemListPosition(0);
            }
        } catch (error: any) {
            dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
            logError(TAG, error);
        }
    };

    const applyBrowseStickiness = () => {
        const collectionSharedPreference = DeviceModel.getSharedPreferences(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_PREF_KEY,
        );
        const stickinessInfo = collectionSharedPreference.getJson(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_INFO,
        ) as any;
        const { unifiedCollectionId = '', partnerId = '' } = getCollectionScreenInfo() || {};
        const key = getBrowseStickinessKey(unifiedCollectionId, partnerId);
        if (stickinessInfo && key && stickinessInfo[key]) {
            // eslint-disable-next-line no-restricted-syntax
            for (const filter in collectionScreenState.filters) {
                if (
                    stickinessInfo[key][filter] &&
                    filter !== CollectionFilters.GET_THIS_SHOW_DETAILS &&
                    filter !== CollectionFilters.ONEPASS_OPTIONS_DETAIL &&
                    filter !== CollectionFilters.MORE_INFO_DETAIL
                ) {
                    const {
                        offset,
                        lastPosition,
                        sortOrder: sortedOrder,
                        isExactPageScrolled,
                    } = stickinessInfo[key][filter];
                    dispatch(
                        setCollectionScreenFilterDetailsAction(filter, {
                            episodes: collectionScreenState.filters[filter].episodes,
                            offset,
                            limit: COLLECTION_SCREEN_ITEMS_LIST_LIMIT,
                            lastPosition,
                            sortOrder: sortedOrder,
                            isExactPageScrolled,
                        }),
                    );
                }
            }
        }
    };

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_RESUME) {
            /* When the user sends the app to the background in cursor mode and returns to
         the foreground in keyboard mode, handle key navigation appropriately. */
            if (isScreenMounted.current && !collectionScreenState.isLoadingFilterItems) {
                dispatch(onCursorStateChangeCollectionScreenOperations());
            }
            if (NetworkManager.isNetworkDisconnected()) dispatch(showGuideScreen());
        }
    }, [visibilityState]);

    useEffect(() => {
        const selectedId =
            itemslistEntry?.position !== undefined
                ? getContentIdSelection(itemslistEntry?.position)
                : '';
        if (filterslistEntry?.isFocused || !selectedId) {
            getTopPreviewForUCId();
        } else {
            fetchTopPreview(selectedId);
        }
    }, [filterslistEntry?.isFocused, itemslistEntry?.position, selectedFilter]);

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        UIOrchestrator.stateChangeListener.subscribe(handleCollectionScreenRefresh);
        isScreenMounted.current = true;
        fetchFilters()
            .then(() => {
                applyBrowseStickiness();
            })
            .catch((error) => {
                Log.e(TAG, 'fetchFilters error', error);
            });
        return () => {
            isScreenMounted.current = false;
            ndvrServices.clearSeasonsData();
            ndvrServices.clearCachedOffers();
            dispatch(resetAllCollectionScreenFiltersAction());
            if (isVisitedUpcomingFilter.current) ndvrServices.clearUpcomingOfferCache();
            CursorManager.unsubscribe(refCursorManager);
            UIOrchestrator.stateChangeListener.cancel(handleCollectionScreenRefresh);
        };
    }, [refreshCollectionScreen]);

    useEffect(() => {
        dispatch(setCollectionFilterItemLoading(true));
        if (collectionScreenState.isLastPage) dispatch(setCollectionIsLastPage(false));
        if (filters.length) {
            fetchItems();
        }
        // TODO : Need to update dependency list and optimize this
    }, [filterPosition, filters, sortOrder]);

    useEffect(() => {
        if (!collectionScreenState.isLoadingFilterItems && !initialFocus && filters.length) {
            const initialPosition = updateAndGetInitialFilterFocus();
            const initialFilter = getSelectedFilterFromPosition(initialPosition);
            setInitialFocus(true);
            if (isModifiedOnepassAction.current) isModifiedOnepassAction.current = false;
            if (isDeletedOnepassAction.current) isDeletedOnepassAction.current = false;
            handleInitialItemFocus(initialFilter);
        }
    }, [collectionScreenState.isLoadingFilterItems]);

    useEffect(() => {
        Diagnostics.info(TAG, 'CollectionScreen mounted');
        if (NetworkManager.isNetworkDisconnected()) {
            const message = NetworkManager.getNetworkDisconnectedMessage();
            dispatch(showWhisper({ message }));
        } else {
            dispatch(hideWhisper());
        }

        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            Diagnostics.info(TAG, 'CollectionScreen un-mounted');

            NetworkManager.unsubscribe(refNetworkManager);
            new VodInteractor().clearPersonContentServiceCache();
        };
    }, []);

    return (
        <React.Fragment>
            <div className="cs-container">
                {
                    <TopPreview
                        preview={previewForUCId}
                        completeOfferPreviewReceived={completeOfferPreviewReceived}
                        isVod={
                            selectedFilter ===
                            CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL
                        }
                    />
                }
                <div className="cs-body-layout">
                    <div className="cs-filters">
                        <List
                            type={ListTypes.COLLECTION_FILTERS}
                            itemList={filters}
                            generateItemView={(item, itemClass, index) =>
                                renderFilterItem(item, itemClass, index || 0)
                            }
                        />
                    </div>
                    <div className="cs-items">{renderItems()}</div>
                </div>
                <ScreenFooter image={secondaryBranding}>
                    {selectedFilter === CollectionFilters.WATCH_LIST_DETAILS ||
                    selectedFilter === CollectionFilters.RECORDINGS_DETAILS ? (
                        <ul className="cs-footer-list-container">
                            <li className="cs-footer-list-items">
                                {sortOrder === CollectionSortType.BY_DATE
                                    ? `${Locale.getString(LocaleKeys.SORTED_BY_DATE)}`
                                    : `${Locale.getString(LocaleKeys.SORTED_BY_SEASON)}`}
                            </li>
                        </ul>
                    ) : (
                        <></>
                    )}
                </ScreenFooter>
                {isAntiBurnOsdActive && <AntiburnOsd />}
                {DeviceModel.isWebOsPlatform() && isCursorActive ? <InteractiveLayer /> : null}
            </div>
            <Whisper
                message={
                    NetworkManager.isNetworkDisconnected()
                        ? NetworkManager.getNetworkDisconnectedMessage()
                        : ''
                }
                iconType={WhisperTypes.INFO}
            />
        </React.Fragment>
    );
};

export default CollectionScreen;
